import { forwardRef } from 'react';
import clsx from 'clsx';

interface Props {
  id: string;
  /** The question, phrased for the person answering it. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Server-enforced minimum. Keep in step with the DTO's @MinLength. */
  minLength: number;
  /** One-click answers. Each must satisfy minLength. */
  presets?: readonly string[];
  placeholder?: string;
  /** Explains where the text ends up. */
  hint?: string;
  /** True once the user has tried to submit with it incomplete. */
  showError?: boolean;
  /** Accent for the preset chips, matching the surrounding section. */
  tone?: 'accent' | 'lock';
  rows?: number;
}

/**
 * A required justification.
 *
 * Every privileged action in this system — approving access, granting
 * unmasked data, downloading results, writing a policy rule — records why it
 * happened, because "who did this and on what grounds" is the first question
 * an audit asks. That makes these fields mandatory everywhere, and one
 * component keeps them mandatory in the same way: same asterisk, same
 * counter, same presets, same error treatment.
 *
 * The presets exist because a required field with no easy correct answer is
 * how audit logs fill up with "asdf".
 */
export const ReasonField = forwardRef<HTMLTextAreaElement, Props>(function ReasonField(
  { id, label, value, onChange, minLength, presets, placeholder, hint, showError, tone = 'accent', rows },
  ref,
) {
  const short = value.trim().length < minLength;
  const invalid = Boolean(showError) && short;

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-2xs font-medium text-ink-muted">
        {label} <span className="text-danger" title="Required">*</span>
      </label>

      {presets && presets.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {presets.map((preset) => (
            <button
              key={preset}
              type="button"
              className={clsx(
                'chip border-edge bg-surface text-ink-muted',
                tone === 'lock'
                  ? 'hover:border-lock hover:text-lock'
                  : 'hover:border-accent hover:text-accent',
              )}
              onClick={() => onChange(preset)}
            >
              {preset}
            </button>
          ))}
        </div>
      )}

      <textarea
        ref={ref}
        id={id}
        required
        aria-required="true"
        aria-invalid={invalid}
        aria-describedby={`${id}-hint`}
        rows={rows}
        className={clsx(
          'input resize-y leading-relaxed',
          rows ? '' : 'min-h-[64px]',
          invalid && 'border-warn',
        )}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />

      <p id={`${id}-hint`} className="mt-1 text-2xs leading-relaxed text-ink-faint">
        {invalid ? (
          <span className="text-warn">
            Required — at least {minLength} characters ({value.trim().length} so far).
          </span>
        ) : (
          hint
        )}
      </p>
    </div>
  );
});
